import MapView from '@arcgis/core/views/MapView';
import {
  GraphicsLayer,
  SpatialReference,
  FeatureSet,
  Symbol,
  Polyline,
  esriProjection,
  Polygon,
  Point,
  esriGeometryEngine,
  Graphic,
  SimpleFillSymbol,
  Geometry,
  FeatureLayer,
  Extent,
  FieldProperties,
} from './esriTypes';
import { INewPitForm } from 'cd-eft/models/Stores/PitDesignStore';
import { INewPadForm } from 'cd-eft/models/Stores/PadDesignStore';
import { degreesToRadians } from './mathUtilities';
import { __moveRotateSymbol } from 'cd-eft/utils/esriSymbols';
import { appConfig } from 'cd-eft/config/AppConfig';
import stores from '../models/Stores';
import Viewpoint from '@arcgis/core/Viewpoint';
import * as PRJAPI from 'cd-eft/services/eftPrjAdminApi';
import { StateCountyDto } from 'cd-eft/_codegen_/openapi-generator/eft-prjadmin';
import MessageCenter from 'cd-eft/components/common/MessageCenter/MessageCenterVM';

export async function saveScreenshot(
    projectUuid: string,
    screenshotUrl: string,
    showWarning: boolean = true,
    maxAttempts: number = 10
  ): Promise<void> {
  let hasError = false;
  let attempt = 1;
  let done = false;
  for (; ;) {
    if (attempt > maxAttempts) {
      hasError = true;
      done = true;
    }
    if (done) {
      break;
    }
    try {
      await PRJAPI.MetadataImageController.uploadAOIImageBase64StringUsingPOST(projectUuid, screenshotUrl);
      done = true;
    } catch {
      attempt++;
    }
  }
  if (hasError && showWarning) {
    await MessageCenter.addNotificationFromCode('Common_20', [
      {
        callback: () => {
          MessageCenter.clearMessages();
        },
        text: 'OK',
      },
    ]);
  }
}

export function cloneFeatureSet(featureSet: FeatureSet): FeatureSet {
  const json = featureSet.toJSON();
  const result = FeatureSet.fromJSON(json);
  return result;
}

export function computeAcres(polygon: Polygon, spatialReference?: SpatialReference): number {
  if (polygon.spatialReference?.isWebMercator || polygon.spatialReference?.isWGS84) {
    const acres = esriGeometryEngine.geodesicArea(polygon, 'acres');
    return acres;
  } else if (spatialReference) {
    const polygon2 = esriProjection.project(polygon, spatialReference) as Polygon;
    const acres = esriGeometryEngine.planarArea(polygon2, 'acres');
    return acres;
  } else {
    const acres = esriGeometryEngine.planarArea(polygon, 'acres');
    return acres;
  }
}

export function explodeMultipartPolygon(polygon: Polygon): Polygon[] {
  const result: Polygon[] = [];

  const outerRings: number[][][] = [];
  const innerRings: number[][][] = [];

  const polygon2 = polygon.clone();
  for (const ring of polygon2.rings) {
    const newPolygon = new Polygon({
      spatialReference: polygon2.spatialReference,
      rings: [ring],
      hasM: polygon2.hasM,
      hasZ: polygon2.hasZ,
    });

    if (0 < computeAcres(newPolygon)) {
      outerRings.push(ring);
    } else {
      innerRings.push(ring);
    }
  }

  for (const r of outerRings) {
    const rings: number[][][] = [];
    rings.push(r);

    let newPolygon = new Polygon({
      spatialReference: polygon2.spatialReference,
      rings: [r],
      hasM: polygon2.hasM,
      hasZ: polygon2.hasZ,
    });

    for (const ir of innerRings) {
      let isContained = true;
      for (const pt of ir) {
        const newPoint = new Point({
          spatialReference: polygon2.spatialReference,
          hasM: false,
          hasZ: false,
          x: pt[0],
          y: pt[1],
        });
        if (!newPolygon.contains(newPoint)) {
          isContained = false;
          break;
        }
      }
      if (isContained) {
        rings.push(ir);
      }
    }

    newPolygon = new Polygon({
      spatialReference: polygon2.spatialReference,
      rings: rings,
      hasM: polygon2.hasM,
      hasZ: polygon2.hasZ,
    });
    result.push(newPolygon);
  }

  return result;
}

export async function graphicsLayerToFeatureSet(
  graphicsLayer: GraphicsLayer,
  geometryType: 'polyline' | 'polygon',
  targetSpatialReference: SpatialReference
): Promise<FeatureSet> {
  const fs = new FeatureSet({ geometryType, spatialReference: targetSpatialReference });

  if (!esriProjection.isLoaded()) {
    await esriProjection.load();
  }

  //for (const graphic of graphicsLayer.graphics) {
  graphicsLayer.graphics.forEach((graphic) => {
    if (graphic.geometry.type !== geometryType) {
      throw new Error('Unexpected geometry type found in supplied graphics layer.');
    }
    const newGraphic = graphic.clone();
    if (newGraphic.geometry.spatialReference.wkid !== targetSpatialReference.wkid) {
      newGraphic.geometry = esriProjection.project(newGraphic.geometry, targetSpatialReference) as __esri.Geometry;
    }
    fs.features.push(newGraphic);
  });

  return fs;
}

export function featureSetToGraphicsLayer(featureSet: FeatureSet | undefined, symbol: Symbol): GraphicsLayer {
  const gl = new GraphicsLayer();
  featureSet?.features.forEach((graphic) => {
    const g = graphic.clone();
    g.symbol = symbol;
    g.geometry.spatialReference = featureSet?.spatialReference;
    gl.graphics.push(g);
  });
  return gl;
}

function pad(number1: string, number2: string) {
  const parts1 = number1.split('.'),
    parts2 = number2.split('.');

  if (parts1[0].length > parts2[0].length) {
    parts2[0] = new Array(Math.abs(parts1[0].length - parts2[0].length) + 1).join('0') + (parts2[0] ? parts2[0] : '');
  } else {
    parts1[0] = new Array(Math.abs(parts1[0].length - parts2[0].length) + 1).join('0') + (parts1[0] ? parts1[0] : '');
  }

  //pad fractional part
  const length1 = parts1[1] ? parts1[1].length : 0;
  const length2 = parts2[1] ? parts2[1].length : 0;
  if (length1 || length2) {
    if (length1 > length2) {
      parts2[1] = (parts2[1] ? parts2[1] : '') + new Array(Math.abs(length1 - length2) + 1).join('0');
    } else {
      parts1[1] = (parts1[1] ? parts1[1] : '') + new Array(Math.abs(length1 - length2) + 1).join('0');
    }
  }

  number1 = parts1[0] + (parts1[1] ? '.' + parts1[1] : '');
  number2 = parts2[0] + (parts2[1] ? '.' + parts2[1] : '');

  return [number1, number2];
}
function compareTo(number1: string, number2: string) {
  let negative = false;
  if (number1[0] == '-' && number2[0] != '-') {
    return -1;
  } else if (number1[0] != '-' && number2[0] == '-') {
    return 1;
  } else if (number1[0] == '-' && number2[0] == '-') {
    number1 = number1.substr(1);
    number2 = number2.substr(1);
    negative = true;
  }
  [number1, number2] = pad(number1, number2);
  if (number1.localeCompare(number2) == 0) {
    return 0;
  }
  for (let i = 0; i < number1.length; i++) {
    if (number1[i] == number2[i]) {
      continue;
    } else if (number1[i] > number2[i]) {
      if (negative) {
        return -1;
      } else {
        return 1;
      }
    } else {
      if (negative) {
        return 1;
      } else {
        return -1;
      }
    }
  }
  return 0;
}

/**
 * Insert a new line in between 2 lines in pure math format, i.e., [x, y, z].
 *
 * @param line1
 * @param line2
 * @param cutBegin: positive number will shorten the line from the begining; negative extend the line.
 * @param cutEnd: positive number will shorten the line from the end; negative extend the line.
 * @returns: [x, y, z] array for pahts of a new polyline geometry.
 */
export function insertNewLineInBetweenTwoLines(line1: any, line2: any, cutBegin = 0.25, cutEnd = 0.3) {
  // calculate middle line coordinates
  let beginX = (line1[0].x + line2[0].x) / 2;
  let beginY = (line1[0].y + line2[0].y) / 2;

  let endX = (line1[1].x + line2[1].x) / 2;
  let endY = (line1[1].y + line2[1].y) / 2;

  let beginZ = (line1[0].z + line2[0].z) / 2;
  let endZ = (line1[1].z + line2[1].z) / 2;

  // cut off begin segment if required
  if (cutBegin !== 0) {
    const cutX = Math.abs(beginX - endX) * cutBegin;
    const cutY = Math.abs(beginY - endY) * cutBegin;
    const cutZ = Math.abs(beginZ - endZ) * cutBegin;

    if (endX > beginX) {
      beginX += cutX;
    } else {
      beginX -= cutX;
    }

    if (endY > beginY) {
      beginY += cutY;
    } else {
      beginY -= cutY;
    }

    if (endZ > beginZ) {
      beginZ += cutZ;
    } else {
      beginZ -= cutZ;
    }
  }

  // cut off end segment if required
  if (cutEnd !== 0) {
    const cutX = Math.abs(beginX - endX) * cutEnd;
    const cutY = Math.abs(beginY - endY) * cutEnd;
    const cutZ = Math.abs(beginZ - endZ) * cutEnd;

    if (endX > beginX) {
      endX -= cutX;
    } else {
      endX += cutX;
    }

    if (endY > beginY) {
      endY -= cutY;
    } else {
      endY += cutY;
    }

    if (endZ > beginZ) {
      endZ -= cutZ;
    } else {
      endZ += cutZ;
    }
  }

  // finally, assembly the path for the new line
  const beginPoint = [beginX, beginY, beginZ];
  const endPoint = [endX, endY, endZ];
  // console.log('gisUtils : begin z : ', beginZ, ' end z : ', endZ);
  const z1Str = beginZ.toString();
  const z2Str = endZ.toString();
  //console.log('z1Str = ', z1Str, ' z2Str = ', z2Str, ' comparing : ', compareTo(z1Str, z2Str));
  //console.log('- - - - - - - - -');
  if (compareTo(z1Str, z2Str) === 1) return [beginPoint, endPoint];

  return [beginPoint, endPoint];
}

/**
 * Create a graphic representation of a rectangular structure on the provided mapView.
 * Polygon bounds and orientation defined by designForm inputs.
 * Used for Rectangular and Diked pits and Rectangular Pads.
 *
 * @param mapView MapView containing the baseStructureGraphics layer with the structure representation.
 * @param designForm Store design form object containing inputs for a structure.
 */
export function drawRectangularBase(mapView: MapView, designForm: INewPadForm | INewPitForm, projectSR: SpatialReference) {
  let layer = mapView.map.findLayerById('baseStructureGraphics') as GraphicsLayer;
  if (!layer) {
    layer = new GraphicsLayer({ id: 'baseStructureGraphics', title: 'baseStructureGraphics' });
    mapView.map.add(layer);
  }
  layer.graphics.removeAll();
  const lengthWidthVars: string[] = ['length', 'width'];
  if (designForm.type === 'Diked Pit') {
    lengthWidthVars[0] = 'bottomLength';
    lengthWidthVars[1] = 'bottomWidth';
  }
  const widthOffset = designForm[lengthWidthVars[1]] / 2.0;
  const lengthOffset = designForm[lengthWidthVars[0]] / 2.0;
  const cx = designForm.center.x;
  const cy = designForm.center.y;
  const geom = new Polygon({ spatialReference: projectSR });
  geom.addRing(createPointsArray(widthOffset, lengthOffset, cx, cy, projectSR));
  const geomRotate = esriGeometryEngine.rotate(
    geom,
    designForm.orienAzimuth * -1,
    new Point({ x: cx, y: cy, spatialReference: projectSR })
  );

  const geom2 = esriProjection.project(geomRotate, mapView.spatialReference) as Geometry;

  const graphic = new Graphic({ geometry: geom2, symbol: __moveRotateSymbol });
  layer.graphics.add(graphic);
}

/**
 * Create a graphic representation of a circular structure on the provided mapView.
 * Draws a rough circle with 30 points every 12 degrees along the circle.
 *
 * @param mapView MapView containing the baseStructureGraphics layer with the structure representation.
 * @param designForm Store design form object containing inputs for a structure.
 */
export function drawCircleBase(mapView: MapView, designForm: INewPitForm, projectSR: SpatialReference) {
  let layer = mapView.map.findLayerById('baseStructureGraphics') as GraphicsLayer;
  if (!layer) {
    layer = new GraphicsLayer({ id: 'baseStructureGraphics', title: 'baseStructureGraphics' });
    mapView.map.add(layer);
  }
  layer.graphics.removeAll();
  const radius = designForm.radius;
  const cx = designForm.center.x;
  const cy = designForm.center.y;
  const geom = new Polygon({ spatialReference: projectSR! });
  geom.addRing(createCircularPointsArray(radius, cx, cy, 30, projectSR));
  const geom2 = esriProjection.project(geom, mapView.spatialReference) as Geometry;

  const graphic = new Graphic({ geometry: geom2, symbol: __moveRotateSymbol });
  layer.graphics.add(graphic);
}

/**
 * Create a rectangular polygon given length/2, width/2, and center coordinates.
 * Uses the spatial reference wkid 32164 to construct the polygon.
 *
 * @param widthOffset The absolute x distance of each point from the center. Derived from the total width/2
 * @param lengthOffset The absolute y distance of each point from the center. Derived from the total length/2
 * @param cx Coordinate x value of the center point
 * @param cy Coordinate y value of the center point
 * @returns An array of points making up all 4 corners of the rectangle.
 */
function createPointsArray(widthOffset, lengthOffset, cx, cy, projectSR) {
  const point0 = new Point({ x: cx - widthOffset, y: cy - lengthOffset, spatialReference: projectSR });
  const point1 = new Point({ x: cx - widthOffset, y: cy + lengthOffset, spatialReference: projectSR });
  const point2 = new Point({ x: cx + widthOffset, y: cy + lengthOffset, spatialReference: projectSR });
  const point3 = new Point({ x: cx + widthOffset, y: cy - lengthOffset, spatialReference: projectSR });
  const point4 = new Point({ x: cx - widthOffset, y: cy - lengthOffset, spatialReference: projectSR });
  return [point0, point1, point2, point3, point4];
}

/**
 * Create a circular polygon given the radius, center coordinates, and number of points making up the circle.
 * Increasing the points arg can make the circle more circular.
 * Uses the spatial reference wkid 32164 to construct the polygon.
 *
 * @param radius  The input radius of the circle
 * @param cx  the center x coordinate of the circle
 * @param cy  the center y coordinate of the circle
 * @param points  the number of points used to make up the circle.
 * @returns an array of length points making up the circle.
 */
function createCircularPointsArray(radius, cx, cy, points, projectSR) {
  const pointsArray: Point[] = [];
  let angle = 0;
  const step = 360 / points;
  for (let i = 0; i < points; i++) {
    const theta = degreesToRadians(angle);
    const point = new Point({
      x: cx + radius * Math.cos(theta),
      y: cy + radius * Math.sin(theta),
      spatialReference: projectSR,
    });
    pointsArray.push(point);
    angle += step;
  }
  return pointsArray;
}

/**
 * Searches the mapView for a layer 'baseStructureGraphics' to read polygon point data from.
 * Data data is used to update center and rotation inputs from the given design form.
 *
 * @param mapView MapView containing the baseStructureGraphics layer with the structure representation.
 * @param designForm Store design form object containing inputs for a structure.
 */
export function updateRectangularGraphic(mapView: MapView, designForm: INewPadForm | INewPitForm, projectSR: SpatialReference) {
  const layer = mapView.map.findLayerById('baseStructureGraphics') as GraphicsLayer;
  if (layer) {
    const rawGeom = layer.graphics.getItemAt(0).geometry;
    const projection = esriProjection.project(rawGeom, projectSR) as Geometry;
    const cx = projection.get('centroid.x') as number;
    const cy = projection.get('centroid.y') as number;
    designForm.center.x = parseFloat(cx.toFixed(2));
    designForm.center.y = parseFloat(cy.toFixed(2));
    const pRings: any = projection.get('rings.0');
    designForm.orienAzimuth = getAngleAzimuth(pRings, projectSR);
  }
}

/**
 * Searches the mapView for a layer 'baseStructureGraphics' to read polygon point data from.
 * Data data is used to update center inputs from the given design form.
 *
 * @param mapView MapView containing the baseStructureGraphics layer with the structure representation.
 * @param designForm Store design form object containing inputs for a structure.
 */
export function updateCircleGraphic(mapView: MapView, designForm: INewPitForm, projectSR: SpatialReference) {
  const layer = mapView.map.findLayerById('baseStructureGraphics') as GraphicsLayer;
  if (layer) {
    const rawGeom = layer.graphics.getItemAt(0).geometry;
    const projection = esriProjection.project(rawGeom, projectSR) as Geometry;
    const cx = projection.get('centroid.x') as number;
    const cy = projection.get('centroid.y') as number;
    designForm.center.x = parseFloat(cx.toFixed(2));
    designForm.center.y = parseFloat(cy.toFixed(2));
  }
}

/**
 * Calculates the distance between 3 Points in a rectangle to calculate the width and length of the rectangle.
 *
 * @param pointArray An array containing at least 3 Points
 * @returns a number array of [width, length]
 */
function getDistance(pointArray, projectSR) {
  const point0 = new Point({ x: pointArray[0][0], y: pointArray[0][1], spatialReference: projectSR });
  const point1 = new Point({ x: pointArray[1][0], y: pointArray[1][1], spatialReference: projectSR });
  const point2 = new Point({ x: pointArray[2][0], y: pointArray[2][1], spatialReference: projectSR });
  const width: number = Math.round(point1.distance(point2));
  const length: number = Math.round(point1.distance(point0));
  return [width, length];
}

/**
 * Calculates the rotation angle in degrees of a polygon given its array of points. Implemented for rectangles.
 * @param pointArray an array of points making up a polygon
 * @returns the clockwise rotation in degrees from true north (azimuth)
 */
function getAngleAzimuth(pointArray, projectSR) {
  const point1 = new Point({ x: pointArray[1][0], y: pointArray[1][1], spatialReference: projectSR });
  const point2 = new Point({ x: pointArray[2][0], y: pointArray[2][1], spatialReference: projectSR });
  const dx = point2.x - point1.x;
  const dy = point2.y - point1.y;
  const radians: number = Math.atan2(dy, dx);
  return getAngleDegrees(radians);
}
/**
 * Translates an angle in radians to degrees. Fixed to 2 decimal places
 * @param radians the angle in radians
 * @returns  the angle in degrees
 */
function getAngleDegrees(radians) {
  let degrees: number = ((radians * 180) / Math.PI) * -1;
  if (degrees < 0) {
    degrees += 360; // Correct for negative angles
  }
  return parseFloat(degrees.toFixed(2));
}

/**
 * wkid: 32164
 */
const __spatialReference = new SpatialReference({
  wkid: appConfig.tempHardCodes.wkid,
});

export async function getUtmZoneWkid(point: Point): Promise<number> {
  if (point.spatialReference) {
    let p = point;
    if (!point.spatialReference.isWGS84) {
      if (!esriProjection.isLoaded()) {
        await esriProjection.load();
      }
      p = esriProjection.project(point, SpatialReference.WGS84) as Point;
    }
    if (p && (p.latitude > -80) && (p.latitude < 84)) {
      const epsgBaseCode: number = ((p.latitude > 0) ? 32600 : 32700);
      let utmZone = Math.floor(p.longitude / 6 + 31);
      if ((utmZone < 1) || (utmZone > 60)) {
        utmZone = 1;
      }
      return (epsgBaseCode + utmZone);
    }
  }
  return -1;
}

export function removeLayers(layers, mapView: MapView) {
  while (layers.length > 0) {
    const layer = layers.shift();
    if (layer) {
      mapView.map.remove(layer);
    }
  }
}

//Helper function for map zoom in to target state and county
export async function zoomToSubjectArea(mapView: MapView, ProjectVM, designStore): Promise<void> {

  await getProjectCountyExtent(designStore, ProjectVM);
  if (ProjectVM.countyExtent !== undefined) {
    if (stores.AppStore.efh2Store.homeWidget) {
      stores.AppStore.efh2Store.homeWidget!.viewpoint = new Viewpoint({
        targetGeometry: ProjectVM.countyExtent,
        rotation: 0,
      });
    }
  }

  if (ProjectVM.countyExtent !== undefined) {
    //zooming to viewpoint ensures that rotation orientation is reset back to 0
    const viewpoint = new Viewpoint();
    viewpoint.targetGeometry = ProjectVM.countyExtent;
    viewpoint.rotation = 0;
    mapView.goTo(viewpoint);
  }
}

export async function getProjectCountyExtent(designStore, ProjectVM): Promise<void> {
  const project = designStore?.projectsStore.currentProject;
  if (project && project.state !== '') {
    const response = await PRJAPI.StateCountyController.getCountiesByStateUsingGET(project.state);
    const counties = response.data;
    if (project.county === '') {
      // compute state bounding coordinates
      let stateXMin: number | undefined = undefined;
      let stateXMax: number | undefined = undefined;
      let stateYMin: number | undefined = undefined;
      let stateYMax: number | undefined = undefined;
      for (const co of counties) {
        normalizeCountyXRange(co);
        if ((co.xmin !== undefined) && (stateXMin === undefined || stateXMin > co.xmin)) {
          stateXMin = co.xmin;
        }
        if ((co.xmax !== undefined) && (stateXMax === undefined || stateXMax < co.xmax)) {
          stateXMax = co.xmax;
        }
        if ((co.ymin !== undefined) && (stateYMin === undefined || stateYMin > co.ymin)) {
          stateYMin = co.ymin;
        }
        if ((co.ymax !== undefined) && (stateYMax === undefined || stateYMax < co.ymax)) {
          stateYMax = co.ymax;
        }
      }
      if (
        stateXMin !== undefined &&
        stateXMax !== undefined &&
        stateYMin !== undefined &&
        stateYMax !== undefined
      ) {
        ProjectVM.countyExtent = new Extent({
          xmin: stateXMin,
          xmax: stateXMax,
          ymin: stateYMin,
          ymax: stateYMax,
          spatialReference: new SpatialReference({ wkid: 4326 }),
        }) as Geometry;
      }
      return;
    }
    const county = counties.find((co) => {
      return co.county_name === project.county;
    });
    if (
      county &&
      county.xmin !== undefined &&
      county.xmax !== undefined &&
      county.ymin !== undefined &&
      county.ymax !== undefined
    ) {
      normalizeCountyXRange(county);
      ProjectVM.countyExtent = new Extent({
        xmin: county.xmin,
        xmax: county.xmax,
        ymin: county.ymin,
        ymax: county.ymax,
        spatialReference: new SpatialReference({ wkid: 4326 }),
      }) as Geometry;
    }
  }
}

export function normalizeCountyXRange(county: StateCountyDto): void {
  if (
    county.xmin !== undefined &&
    county.xmax !== undefined &&
    county.xmax - county.xmin > 10000000 &&
    county.xmax > 0 &&
    county.xmin < 0
  ) {
    const minX = county.xmax + -20026376.39 * 2.0;
    county.xmax = county.xmin;
    county.xmin = minX;
  }
}

export function putStructureRepOnTop(mapView: MapView, layerName = '') {
  const layer = mapView.map.findLayerById('baseStructureGraphics') as GraphicsLayer;
  if (layer) {
    mapView.map.reorder(layer, mapView.map.layers.length - 1);
  }
  if (layerName !== '') {
    const layer = mapView.map.findLayerById(layerName) as GraphicsLayer;
    mapView.map.reorder(layer, mapView.map.layers.length - 1);
  }
}

export function createPointListBoundaryLayer(pointLayer: FeatureLayer): GraphicsLayer {
  const points: any[] = pointLayer.source.toArray().map((graphic) => {
    return graphic.geometry;
  });

  //close the ring
  points.push(points[0]);

  const convexSymbol = new SimpleFillSymbol({
    style: 'none',
    outline: {
      color: 'black',
      width: 2,
    },
  });

  const polygon = new Polygon();
  polygon.addRing(points);
  polygon.spatialReference = points[0].spatialReference;

  const graphic = new Graphic({ geometry: polygon, symbol: convexSymbol });
  const layer = new GraphicsLayer();
  layer.add(graphic);

  return layer;
}

// Check if a given point(x,y) is inside a polygon
export async function isPointInsidePolygon(x: number, y: number, inPolygon: Polygon, projectSR: SpatialReference) {

  const spatialRef = projectSR;
  const bpPoint = new Point({ x, y, spatialReference: spatialRef });

  await esriProjection.load();

  // const graphicGeom = inLayer.graphics.getItemAt(0).geometry as Polygon;
  const projPoint = esriProjection.project(bpPoint, inPolygon.spatialReference) as Point;

  if (inPolygon && inPolygon.contains(projPoint)) {
    return true;
  }

  return false;
}

// Return a the current surface as polygon.
export function getBoundaryPolygon(projectSR): Polygon {

  const bPts: Point[] = [];
  for (let itm = 0; itm < stores.AppStore.designStore.currentSurface.borderVector.length; itm++) {
    const bp: Point = new Point({
      x: stores.AppStore.designStore.currentSurface.borderVector[itm].x,
      y: stores.AppStore.designStore.currentSurface.borderVector[itm].y,
      z: 0,
    });
    bPts.push(bp);
  }

  const boundaryPoly = new Polygon();
  boundaryPoly.spatialReference = projectSR;
  boundaryPoly.addRing(bPts);

  return boundaryPoly;
}

export function getStructureExteriorPolygon(structureGeometry: Polyline, mapSR): Polygon {
  const pts: Point[] = [];
  const path = structureGeometry.paths[0];
  for (const point of path) {
    const p: Point = new Point({
      x: point[0],
      y: point[1],
      z: 0,
    });
    pts.push(p);
  }
  
  const structurePoly = new Polygon({
    spatialReference: mapSR,
  });
  structurePoly.addRing(pts);
  return structurePoly;
}

export function isSurveyPoint(x: number, y: number) {
  let elevation = 0;
  const designStore = stores.AppStore.designStore;
  if (designStore.surveyPoints && designStore.surveyPoints.length > 0) {
    for (const point of designStore.surveyPoints) {
      if (point.x.toFixed(0) === x.toFixed(0) && point.y.toFixed(0) === y.toFixed(0)) {
        elevation = point.z;
      }
    }
  }

  // if elevation === 0, it is not a border point
  return elevation;
}

/**
 * This function accepts an array of point objects (must at least include x and y coordinates), converts them to ESRI Point type if needed, creates 
 * a Polyline out of the Points, then checks if the line self-intersects.
 */
export function doesPolylineSelfIntersect<T extends { x: number, y: number }>(points: (T | Point)[], spatialReference): boolean {
  if (points.length < 4) return false;

  const pointsArray: Point[] = [];
  for (const point of points) {
    if (point instanceof Point) {
      pointsArray.push(point);
    } else {
      pointsArray.push(new Point({
        x: point.x,
        y: point.y,
        spatialReference: spatialReference
      }));
    }
  }

  const segments: Polyline[] = [];
  for (let i = 0; i < points.length - 1; i++) {
    const start = points[i];
    const end = points[i + 1];

    segments.push(
      new Polyline({
        spatialReference: spatialReference,
        paths: [[[start.x, start.y], [end.x, end.y]]]
      })
    );
  }

  // Compare non‑adjacent segments
  for (let i = 0; i < segments.length; i++) {
    for (let j = i + 1; j < segments.length; j++) {
      if (Math.abs(i - j) <= 1) continue;
      if (esriGeometryEngine.crosses(segments[i], segments[j])) {
        return true;
      }
    }
  }

  return false;
}


/**
 * Common field definitions for feature layers
 */
export const commonFields: FieldProperties[] = [
  {
    name: 'ObjectID',
    alias: 'ObjectID',
    type: 'oid',
  },
  {
    name: 'pointName',
    alias: 'PointName',
    type: 'string',
  },
  {
    name: 'y',
    alias: 'y',
    type: 'double',
  },
  {
    name: 'x',
    alias: 'x',
    type: 'double',
  },
  {
    name: 'z',
    alias: 'z',
    type: 'double',
  },
  {
    name: 'description',
    alias: 'description',
    type: 'string',
  },
];